import { AssignStatus } from "@/enum/list.enum";
import { ROUTE_CONFIG } from "@/routes/config";

import {
	LIST_SCOPE_RELEVANCE_STATUSES,
	ListScope,
	listIdsParam,
} from "@/pages/Private/pages/Messages/components/ListScopeFilter/listScope";

import { buildListDetailLink } from "../../List/utils/buildListDetailLink";

export type LeadLocationKind = "INBOX" | "OUTBOX" | "LIST" | "CONTACTS";

// The status sets the destination views actually filter by, read from the one module that owns
// them (`listScope.ts`) rather than restated here — a private copy is how a lead's resolved
// location drifts from where that view would surface it. Anything in neither set is still on a
// list (DRAFT, SAVED_FOR_LATER, …) and resolves to the list detail.
const INBOX_STATUSES = LIST_SCOPE_RELEVANCE_STATUSES[ListScope.INBOX];

const OUTBOX_STATUSES = LIST_SCOPE_RELEVANCE_STATUSES[ListScope.OUTBOX];

export interface LeadLocationInput {
	assignStatus?: AssignStatus;
	listId?: number | null;
	listName?: string | null;
	prospectName?: string | null;
}

export interface LeadLocation {
	kind: LeadLocationKind;
	/** Absolute route to navigate to, pre-filtered to the lead's list/name where the view supports it. */
	to: string;
}

/**
 * Resolve where an existing lead currently lives (Inbox / Outbox / a List / Contacts)
 * and the pre-filtered route to reach it. Pragmatic navigation — it lands the user on the
 * right view filtered to the lead's list and name (Inbox/Outbox/List all seed the text
 * search from the URL), rather than auto-selecting the exact lead (Inbox/Outbox have no
 * per-lead deep-link).
 */
export const resolveLeadLocation = ({
	assignStatus,
	listId,
	listName,
	prospectName,
}: LeadLocationInput): LeadLocation => {
	const hasList = typeof listId === "number";

	// Inbox — seeds both the text search and the list filter from the URL.
	if (assignStatus && INBOX_STATUSES.includes(assignStatus)) {
		const params = new URLSearchParams();

		if (prospectName) {
			params.set("searchValue", prospectName);
		}

		if (hasList) {
			params.set("listIds", listIdsParam(listId, listName));
		}

		const search = params.toString();

		return {
			kind: "INBOX",
			to: search
				? `${ROUTE_CONFIG.MESSAGE_CENTER_INBOX}?${search}`
				: ROUTE_CONFIG.MESSAGE_CENTER_INBOX,
		};
	}

	// Outbox — seeds both the text search and the list filter from the URL.
	if (assignStatus && OUTBOX_STATUSES.includes(assignStatus)) {
		const params = new URLSearchParams();

		if (prospectName) {
			params.set("searchValue", prospectName);
		}

		if (hasList) {
			params.set("listIds", listIdsParam(listId, listName));
		}

		const search = params.toString();

		return {
			kind: "OUTBOX",
			to: search
				? `${ROUTE_CONFIG.MESSAGE_CENTER_OUTBOX}?${search}`
				: ROUTE_CONFIG.MESSAGE_CENTER_OUTBOX,
		};
	}

	// Still on a list — the shared list-detail deep-link (list detail, right tab, name search).
	if (hasList) {
		return { kind: "LIST", to: buildListDetailLink({ listId, prospectName, assignStatus }) };
	}

	// No list membership — the lead only lives in Contacts.
	return { kind: "CONTACTS", to: ROUTE_CONFIG.CONTACTS };
};
